import React, { useState, useEffect, useRef } from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  Link,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemIcon,
  Divider,
  Select,
  FormControl,
  InputLabel,
  DialogContentText,
  TextField,
  Slider
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import PinterestIcon from '@mui/icons-material/Pinterest';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Save } from '@mui/icons-material';
import ElementStyleEditorModal from './ElementStyleEditorModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal'; // ✅ YENİ: Resim kütüphanesi modalı
import { getFooterSettings, saveFooterSettings as saveFooterSettingsAPI } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';

// QuickAccessColumn interface
interface QuickAccessColumn {
  id: number;
  title?: string;
  maxLinks: number;
  hasFixedLinks?: boolean;
}

// QuickLink interface
interface QuickLink {
  id: string;
  label: string;
  url: string;
  column: number;
}

const MAX_QUICK_ACCESS_COLUMNS = 4;

const DEFAULT_QUICK_ACCESS_COLUMNS: QuickAccessColumn[] = [
  { id: 1, maxLinks: 7, hasFixedLinks: true },
  { id: 2, maxLinks: 10, hasFixedLinks: false },
];

const hasSavedQuickAccessColumnConfig = (
  columns: QuickAccessColumn[] | null | undefined,
  elementStyleColumns: QuickAccessColumn[] | null | undefined
): boolean => (columns?.length ?? 0) > 0 || (elementStyleColumns?.length ?? 0) > 0;

const hasCustomColumnTitles = (columns: QuickAccessColumn[]): boolean =>
  columns.some((column) => Boolean(column.title?.trim()));

const shouldPersistQuickAccessColumns = (columns: QuickAccessColumn[]): boolean =>
  hasCustomColumnTitles(columns) || columns.length > 2;

const resolveQuickAccessColumns = (
  columns: QuickAccessColumn[] | null | undefined,
  elementStyleColumns: QuickAccessColumn[] | null | undefined,
  quickLinks: QuickLink[] = []
): QuickAccessColumn[] => {
  const fromJson = columns ?? [];
  const fromStyles = elementStyleColumns ?? [];
  const hasConfig = hasSavedQuickAccessColumnConfig(columns, elementStyleColumns);

  if (!hasConfig) {
    return normalizeQuickAccessColumns(undefined, quickLinks);
  }

  const metadata = fromStyles.length > 0 ? fromStyles : fromJson;
  const linkMax = quickLinks.reduce((max, link) => Math.max(max, link.column || 1), 0);
  const configMax = metadata.reduce((max, column) => Math.max(max, column.id), 0);
  const requiredCount = Math.max(2, linkMax, configMax);
  const resolved: QuickAccessColumn[] = [];

  for (let id = 1; id <= requiredCount; id += 1) {
    const meta = metadata.find((column) => column.id === id);
    const defaults =
      id === 1
        ? { id, maxLinks: 7, hasFixedLinks: true }
        : { id, maxLinks: 10, hasFixedLinks: false };
    const title = meta?.title?.trim();

    resolved.push({
      ...defaults,
      ...meta,
      id,
      title: title || undefined,
      hasFixedLinks: meta?.hasFixedLinks ?? defaults.hasFixedLinks,
    });
  }

  return resolved;
};

const getQuickAccessColumnHeader = (
  column: QuickAccessColumn,
  columnIndex: number,
  columns: QuickAccessColumn[],
  sectionTitle: string
): string | null => {
  if (hasCustomColumnTitles(columns)) {
    return column.title?.trim() || null;
  }

  if (columnIndex === 0) {
    return sectionTitle.trim() || 'Hızlı Erişim';
  }

  return null;
};

const normalizeQuickAccessColumns = (
  columns: QuickAccessColumn[] | null | undefined,
  quickLinks: QuickLink[] = []
): QuickAccessColumn[] => {
  const maxColumnFromLinks = quickLinks.reduce((max, link) => Math.max(max, link.column || 1), 0);
  const requiredCount = Math.max(2, maxColumnFromLinks);

  if (!columns || columns.length === 0) {
    return Array.from({ length: requiredCount }, (_, index) => {
      const id = index + 1;
      if (id === 1) {
        return { id, maxLinks: 7, hasFixedLinks: true };
      }
      return { id, maxLinks: 10, hasFixedLinks: false };
    });
  }

  const normalized = [...columns].sort((a, b) => a.id - b.id);
  for (let id = 1; id <= requiredCount; id += 1) {
    if (!normalized.some((col) => col.id === id)) {
      normalized.push({
        id,
        maxLinks: 10,
        hasFixedLinks: false,
      });
    }
  }

  return normalized.sort((a, b) => a.id - b.id);
};

const getFooterLinksGridTemplate = (quickAccessColumnCount: number) => {
  const totalColumns = 1 + quickAccessColumnCount;

  return {
    xs: '1fr',
    sm: totalColumns >= 2 ? 'repeat(2, 1fr)' : '1fr',
    md: totalColumns >= 3 ? 'repeat(3, 1fr)' : totalColumns >= 2 ? 'repeat(2, 1fr)' : '1fr',
    lg: totalColumns >= 5 ? 'repeat(5, 1fr)' : `repeat(${totalColumns}, 1fr)`,
  };
};

const getQuickAccessGridTemplate = (columnCount: number) => ({
  xs: '1fr',
  sm: columnCount >= 2 ? 'repeat(2, 1fr)' : '1fr',
  md: columnCount >= 3 ? 'repeat(3, 1fr)' : columnCount >= 2 ? 'repeat(2, 1fr)' : '1fr',
  lg: `repeat(${columnCount}, 1fr)`,
});

// SocialLink interface
interface SocialLink {
  id: string;
  platform: 'instagram' | 'facebook' | 'twitter' | 'linkedin' | 'youtube' | 'pinterest';
  icon: string; // 'icon1' to 'icon5'
  url: string;
}

// Styled components
const SectionTitle = React.memo(({ children, ...props }: any) => (
  <Typography 
    variant="h6" 
    sx={{ 
      fontWeight: 'bold',
      marginBottom: 2,
      marginTop: 3,
      display: 'flex',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        marginRight: 1,
      }
    }}
    {...props}
  >
    {children}
  </Typography>
));

// Footer Settings Props
interface FooterSettingsProps {
  settings: {
    showSocialLinks: boolean;
    showPaymentMethods: boolean;
    showNewsletter: boolean;
    backgroundColor: string;
    textColor: string;
    itemsPerRow: number;
    showRatings: boolean;
    enableQuickView: boolean;
    enableWishlist: boolean;
  };
  onSettingsChange: (setting: keyof FooterSettingsProps['settings'], value: any) => void;
  companyId: string | null;
  configId: string; // ✅ YENİ: SQL için configId
}

const FooterSettings: React.FC<FooterSettingsProps> = ({
  settings,
  onSettingsChange,
  companyId,
  configId // ✅ YENİ
}) => {
  const { t } = useTranslation('salesSystem');
  const currentYear = new Date().getFullYear();
  
  // Örnek şirket bilgileri
  const exampleCompany = {
    name: t('ecommerceFooterSettings.preview.companyName'),
    description: t('ecommerceFooterSettings.preview.companyDescription'),
    address: t('ecommerceFooterSettings.preview.companyAddress'),
    phone: '+90 (212) 123 45 67',
    email: 'info@ornek-sirket.com'
  };

  // Element stilleri için state
  const [elementStyles, setElementStyles] = useState({
    companyName: { fontSize: '1.25rem', fontWeight: 'bold', color: '#ffffff', fontStyle: 'normal', fontFamily: 'Arial' },
    companyDescription: { 
      fontSize: '0.875rem', 
      fontWeight: 400, 
      color: 'rgba(255, 255, 255, 0.8)', 
      fontStyle: 'normal', 
      fontFamily: 'Arial',
      text: t('ecommerceFooterSettings.preview.companyDescription')
    },
    sectionTitle: { fontSize: '1.25rem', fontWeight: 'bold', color: '#ffffff', fontStyle: 'normal', fontFamily: 'Arial' },
    contactInfo: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    quickLinks: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    copyright: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    backgroundColor: { backgroundColor: '#1f2937' },
    newsletter: {
      buttonText: t('ecommerceFooterSettings.newsletter.subscribe'),
      showGender: true,
      image: '',
      imageWidth: 400,
      imageHeight: 350,
      bgColor: '#ffffff',
      imageSpacing: 0,
      mobileImage: '',
      mobileImageWidth: 280,
      mobileImageHeight: 200,
      showImageOnMobile: true,
    } as Record<string, any>
  });

  // Footer ayarları için state
  const [footerSettings, setFooterSettings] = useState({
    backgroundColor: '#1f2937',
    textColor: '#ffffff',
    textOpacity: 0.8,
    companyName: '',
    copyrightText: '',
    contactTitle: '',
    quickLinksTitle: '',
    socialLinks: [] as SocialLink[], // Sosyal medya linkleri için state
    quickLinks: [] as QuickLink[], // Hızlı erişim linkleri için state
    quickAccessColumns: DEFAULT_QUICK_ACCESS_COLUMNS as QuickAccessColumn[],
    socialIconSize: 24, // İkon boyutu için state
    companyLogo: '', // Şirket logosu URL'i
    logoWidth: 150, // Logo genişliği
    logoHeight: 60, // Logo yüksekliği
    companyLogo2: '', // İkinci şirket logosu URL'i
    logo2Width: 150, // İkinci logo genişliği
    logo2Height: 60, // İkinci logo yüksekliği
    // İletişim Bilgileri
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    // Newsletter / Bülten Bölümü
    showNewsletterSection: false,
    newsletterTitle: '',
    newsletterDescription: '',
    newsletterButtonText: t('ecommerceFooterSettings.newsletter.subscribe'),
    showGenderSelection: true,
    newsletterImage: '',
    newsletterImageWidth: 400,
    newsletterImageHeight: 350,
    newsletterBgColor: '#ffffff',
    newsletterImageSpacing: 0,
    newsletterMobileImage: '',
    newsletterMobileImageWidth: 280,
    newsletterMobileImageHeight: 200,
    newsletterShowImageOnMobile: true,
  });

  // ✅ YENİ: Resim kütüphanesi modalı için state
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [currentLogoType, setCurrentLogoType] = useState<'logo1' | 'logo2' | 'newsletter' | 'newsletterMobile'>('logo1');

  // Sağ tık menüsü için state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    target: keyof typeof elementStyles;
  } | null>(null);

  // Stil düzenleme modalı için state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingElementKey, setEditingElementKey] = useState<keyof typeof elementStyles | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Arka plan rengi düzenleme modalı için state
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [tempBackgroundColor, setTempBackgroundColor] = useState('#1f2937');

  // Şirket açıklaması için özel modal state
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [tempDescription, setTempDescription] = useState(t('ecommerceFooterSettings.preview.companyDescription'));
  const [tempDescriptionStyles, setTempDescriptionStyles] = useState({
    fontSize: '0.875rem', 
    fontWeight: 400, 
    color: 'rgba(255, 255, 255, 0.8)', 
    fontStyle: 'normal', 
    fontFamily: 'Arial'
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Sosyal medya modalı için state
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [editingSocialLink, setEditingSocialLink] = useState<SocialLink | null>(null);

  // Sosyal medya silme onayı için state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingSocialLinkId, setDeletingSocialLinkId] = useState<string | null>(null);

  // Hızlı Erişim modalı için state
  const [isQuickLinkModalOpen, setIsQuickLinkModalOpen] = useState(false);
  const [editingQuickLink, setEditingQuickLink] = useState<QuickLink | null>(null);
  const [quickLinkDefaultColumn, setQuickLinkDefaultColumn] = useState<number | undefined>(undefined);

  // Hızlı Erişim silme onayı için state
  const [isQuickLinkDeleteConfirmOpen, setIsQuickLinkDeleteConfirmOpen] = useState(false);
  const [deletingQuickLinkId, setDeletingQuickLinkId] = useState<string | null>(null);

  // Hızlı Erişim sütun silme onayı için state
  const [isColumnDeleteConfirmOpen, setIsColumnDeleteConfirmOpen] = useState(false);
  const [deletingColumnId, setDeletingColumnId] = useState<number | null>(null);
  
  // SQL Footer Settings ID
  const [footerSettingsId, setFooterSettingsId] = useState<string | null>(null);
  
  // ✅ AUTO-SAVE: Debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const footerSettingsRef = useRef(footerSettings);
  const elementStylesRef = useRef(elementStyles);

  useEffect(() => {
    footerSettingsRef.current = footerSettings;
  }, [footerSettings]);

  useEffect(() => {
    elementStylesRef.current = elementStyles;
  }, [elementStyles]);
  
  // ✅ AUTO-SAVE: İlk yükleme kontrolü
  const isInitialLoadRef = useRef(true);

  // ✅ AUTO-SAVE: İletişim bilgileri değişince otomatik kaydet
  useEffect(() => {
    if (isInitialLoadRef.current) return; // İlk yüklemede kaydetme
    
    console.log('📞 [FOOTER] İletişim bilgileri değişti, otomatik kayıt başlatılıyor...');
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveFooterSettings();
    }, 1000);
  }, [footerSettings.contactEmail, footerSettings.contactPhone, footerSettings.contactAddress]);

  // ✅ SQL'den ayarları yükle
  useEffect(() => {
    const loadFooterSettingsFromSQL = async () => {
      if (!configId || !companyId) { // ✅ companyId null check
        console.log('⚠️ [FOOTER SETTINGS] ConfigId veya CompanyId yok, yükleme atlanıyor');
        return;
      }

      console.log('🔄 [FOOTER SETTINGS] SQL den ayarlar yükleniyor...', { configId });

      try {
        const response = await getFooterSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response.success && response.data) {
          console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla yüklendi:', response.data);
          setFooterSettingsId(response.data.id);
          
          // Element stillerini parse et
          let elementStylesData = elementStyles;
          if (response.data.elementStylesJson) {
            try {
              elementStylesData = JSON.parse(response.data.elementStylesJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] ElementStylesJson parse hatası:', e);
            }
          }
          
          // Sosyal medya linklerini parse et
          let socialLinksData: SocialLink[] = [];
          if (response.data.socialLinksJson) {
            try {
              socialLinksData = JSON.parse(response.data.socialLinksJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] SocialLinksJson parse hatası:', e);
            }
          }
          
          // Hızlı erişim linklerini parse et
          let quickLinksData: QuickLink[] = [];
          if (response.data.quickLinksJson) {
            try {
              quickLinksData = JSON.parse(response.data.quickLinksJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] QuickLinksJson parse hatası:', e);
            }
          }

          // Hızlı erişim sütunlarını parse et
          let quickAccessColumnsFromJson: QuickAccessColumn[] = [];
          if (response.data.quickAccessColumnsJson) {
            try {
              quickAccessColumnsFromJson = JSON.parse(response.data.quickAccessColumnsJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] QuickAccessColumnsJson parse hatası:', e);
            }
          }

          const quickAccessColumnsFromStyles = Array.isArray((elementStylesData as Record<string, unknown>)?.quickAccessColumns)
            ? ((elementStylesData as Record<string, unknown>).quickAccessColumns as QuickAccessColumn[])
            : [];

          const quickAccessColumnsData = resolveQuickAccessColumns(
            quickAccessColumnsFromJson,
            quickAccessColumnsFromStyles,
            quickLinksData
          );

          const persistedElementStyles = { ...elementStylesData } as typeof elementStyles;
          if (shouldPersistQuickAccessColumns(quickAccessColumnsData)) {
            (persistedElementStyles as Record<string, unknown>).quickAccessColumns = quickAccessColumnsData;
          } else {
            delete (persistedElementStyles as Record<string, unknown>).quickAccessColumns;
          }

          setElementStyles(persistedElementStyles);
          
          // Footer ayarlarını set et
          setFooterSettings({
            backgroundColor: response.data.backgroundColor || '#1f2937',
            textColor: response.data.textColor || '#ffffff',
            textOpacity: 0.8,
            companyName: response.data.companyName || '',
            copyrightText: response.data.copyrightText || '',
            contactTitle: response.data.contactTitle || 'Contact',
            quickLinksTitle: response.data.quickLinksTitle || 'Quick Access',
            socialLinks: socialLinksData,
            quickLinks: quickLinksData,
            quickAccessColumns: quickAccessColumnsData,
            socialIconSize: response.data.socialIconSize || 24,
            companyLogo: response.data.logoUrl || '',
            logoWidth: response.data.logoWidth || 150,
            logoHeight: response.data.logoHeight || 60,
            companyLogo2: response.data.companyLogo2 || '',
            logo2Width: response.data.logo2Width || 150,
            logo2Height: response.data.logo2Height || 60,
            // İletişim Bilgileri
            contactEmail: response.data.contactEmail || '',
            contactPhone: response.data.contactPhone || '',
            contactAddress: response.data.contactAddress || '',
            // Newsletter / Bülten Bölümü (DB alanları)
            showNewsletterSection: response.data.showNewsletterSection || false,
            newsletterTitle: response.data.newsletterTitle || '',
            newsletterDescription: response.data.newsletterDescription || '',
            // Newsletter ekstra alanları (ElementStylesJson içinden)
            newsletterButtonText: elementStylesData?.newsletter?.buttonText || t('ecommerceFooterSettings.newsletter.subscribe'),
            showGenderSelection: elementStylesData?.newsletter?.showGender !== false,
            newsletterImage: elementStylesData?.newsletter?.image || '',
            newsletterImageWidth: elementStylesData?.newsletter?.imageWidth || 400,
            newsletterImageHeight: elementStylesData?.newsletter?.imageHeight || 350,
            newsletterBgColor: elementStylesData?.newsletter?.bgColor || '#ffffff',
            newsletterImageSpacing: elementStylesData?.newsletter?.imageSpacing || 0,
            newsletterMobileImage: elementStylesData?.newsletter?.mobileImage || '',
            newsletterMobileImageWidth: elementStylesData?.newsletter?.mobileImageWidth || 280,
            newsletterMobileImageHeight: elementStylesData?.newsletter?.mobileImageHeight || 200,
            newsletterShowImageOnMobile: elementStylesData?.newsletter?.showImageOnMobile !== false,
          });
          
          // Şirket açıklamasını set et (boş string de geçerli bir değer olmalı)
          const hasCompanyDescriptionText =
            elementStylesData?.companyDescription &&
            Object.prototype.hasOwnProperty.call(elementStylesData.companyDescription, 'text');
          if (hasCompanyDescriptionText) {
            setTempDescription(String(elementStylesData.companyDescription?.text ?? ''));
          }
          
          // ✅ İlk yükleme tamamlandı, artık değişiklikleri kaydet
          setTimeout(() => {
            isInitialLoadRef.current = false;
          }, 500);
        } else {
          console.log('ℹ️ [FOOTER SETTINGS] Ayar bulunamadı, varsayılan değerler kullanılıyor');
          setTimeout(() => {
            isInitialLoadRef.current = false;
          }, 500);
        }
      } catch (error: any) {
        console.error('❌ [FOOTER SETTINGS] Ayarlar yüklenirken hata:', error);
      }
    };

    loadFooterSettingsFromSQL();
  }, [configId]);

  // Sağ tık menüsünü açan fonksiyon
  const handleContextMenu = (event: React.MouseEvent, target: keyof typeof elementStyles) => {
    event.preventDefault();
    event.stopPropagation(); // Event propagation'ı durdur
    setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4, target } : null);
  };

  // Menüyü kapat
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Menüden "Düzenle" tıklandığında modalı aç
  const handleEditClick = () => {
    if (contextMenu) {
      if (contextMenu.target === 'backgroundColor') {
        // Arka plan rengi için özel modal aç
        setTempBackgroundColor(footerSettings.backgroundColor);
        setIsBackgroundModalOpen(true);
      } else if (contextMenu.target === 'companyDescription') {
        // Şirket açıklaması için özel modal aç
        setIsDescriptionModalOpen(true);
      } else {
        // Diğer text elementler için normal modal aç
        setEditingElementKey(contextMenu.target);
        setIsModalOpen(true);
      }
      handleCloseContextMenu();
    }
  };

  // Modal'da stiller kaydedildiğinde
  const handleStyleSave = (newStyles: any) => {
    if (editingElementKey && editingElementKey !== 'backgroundColor') {
      const updatedStyles = {
        ...elementStyles,
        [editingElementKey]: newStyles,
      };
      setElementStyles(updatedStyles);
      setIsModalOpen(false);
      
      // SQL'e kaydet (debounce olmadan)
      saveFooterSettings({ elementStyles: updatedStyles });
    }
  };

  // Arka plan rengi kaydedildiğinde
  const handleBackgroundColorSave = () => {
    const updatedStyles = {
      ...elementStyles,
      backgroundColor: { backgroundColor: tempBackgroundColor }
    };
    setElementStyles(updatedStyles);
    setFooterSettings(prev => ({
      ...prev,
      backgroundColor: tempBackgroundColor
    }));
    setIsBackgroundModalOpen(false);
    
    // SQL'e kaydet (debounce olmadan)
    saveFooterSettings({ elementStyles: updatedStyles, backgroundColor: tempBackgroundColor });
  };

  // Şirket açıklaması kaydedildiğinde
  const handleDescriptionSave = () => {
    const updatedStyles = {
      ...elementStyles,
      companyDescription: {
        ...tempDescriptionStyles,
        text: tempDescription
      }
    };
    setElementStyles(updatedStyles);
    
    // CompanyDescription'ı footerSettings'e de kaydet
    const updatedFooterSettings = {
      ...footerSettings,
      companyDescription: tempDescription
    };
    setFooterSettings(updatedFooterSettings);
    setIsDescriptionModalOpen(false);
    
    // SQL'e kaydet (debounce olmadan) - sadece elementStyles gönder
    saveFooterSettings({ elementStyles: updatedStyles });
  };

  // ✅ AUTO-SAVE: Otomatik kaydetme fonksiyonu (debounce ile)
  const autoSaveFooterSettings = (settingsToSave?: Partial<typeof footerSettings> & { elementStyles?: typeof elementStyles }) => {
    if (!companyId || !configId) {
      console.log('⚠️ [FOOTER AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor', { companyId, configId });
      return;
    }

    // Önceki timer'ı iptal et
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Yeni timer başlat (1 saniye sonra kaydet)
    autoSaveTimerRef.current = setTimeout(async () => {
      console.log('💾 [FOOTER AUTO-SAVE] Otomatik kayıt başlatılıyor...');
      await saveFooterSettings(settingsToSave);
    }, 1000);
  };

  // ✅ Footer stillerini SQL'e kaydet
  const saveFooterSettings = async (settingsToSave?: Partial<typeof footerSettings> & { elementStyles?: typeof elementStyles }) => {
    if (!companyId || !configId) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.companyOrConfigMissing'),
        severity: 'error'
      });
      return;
    }
    
    console.log('💾 [FOOTER SETTINGS] Kaydedilecek ayarlar:', settingsToSave);
    console.log('💾 [FOOTER SETTINGS] Mevcut footerSettings:', footerSettings);
    console.log('💾 [FOOTER SETTINGS] Mevcut elementStyles:', elementStyles);
    
    setIsSaving(true);
    try {
      // JSON alanlarını stringify et
      const baseElementStyles = settingsToSave?.elementStyles || elementStylesRef.current;
      const currentFs = { ...footerSettingsRef.current, ...settingsToSave };
      const quickAccessColumnsToSave = currentFs.quickAccessColumns;
      const persistQuickAccessColumns = shouldPersistQuickAccessColumns(quickAccessColumnsToSave);
      const elementStylesToSave = {
        ...baseElementStyles,
        newsletter: {
          buttonText: currentFs.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe'),
          showGender: currentFs.showGenderSelection !== false,
          image: currentFs.newsletterImage || '',
          imageWidth: currentFs.newsletterImageWidth || 400,
          imageHeight: currentFs.newsletterImageHeight || 350,
          bgColor: currentFs.newsletterBgColor || '#ffffff',
          imageSpacing: currentFs.newsletterImageSpacing || 0,
          mobileImage: currentFs.newsletterMobileImage || '',
          mobileImageWidth: currentFs.newsletterMobileImageWidth || 280,
          mobileImageHeight: currentFs.newsletterMobileImageHeight || 200,
          showImageOnMobile: currentFs.newsletterShowImageOnMobile !== false,
        },
      } as typeof elementStyles & { quickAccessColumns?: QuickAccessColumn[] };

      if (persistQuickAccessColumns) {
        elementStylesToSave.quickAccessColumns = quickAccessColumnsToSave;
      } else {
        delete elementStylesToSave.quickAccessColumns;
      }
      const socialLinksToSave = currentFs.socialLinks;
      const quickLinksToSave = currentFs.quickLinks;
      
      const elementStylesJson = JSON.stringify(elementStylesToSave);
      const socialLinksJson = JSON.stringify(socialLinksToSave);
      const quickLinksJson = JSON.stringify(quickLinksToSave);
      const quickAccessColumnsJson = persistQuickAccessColumns
        ? JSON.stringify(quickAccessColumnsToSave)
        : undefined;
      
      const data = {
        id: footerSettingsId,
        companyId: companyId,
        configId: configId,
        showFooter: true,
        footerLayout: 'layout1',
        columnCount: 3,
        backgroundColor: settingsToSave?.backgroundColor || footerSettings.backgroundColor,
        textColor: settingsToSave?.textColor || footerSettings.textColor,
        linkColor: elementStylesToSave.quickLinks?.color || 'rgba(255, 255, 255, 0.8)',
        showLogo: !!(settingsToSave?.companyLogo || footerSettings.companyLogo),
        logoUrl: settingsToSave?.companyLogo || footerSettings.companyLogo || null,
        logoWidth: settingsToSave?.logoWidth || footerSettings.logoWidth,
        logoHeight: settingsToSave?.logoHeight || footerSettings.logoHeight,
        companyLogo2: settingsToSave?.companyLogo2 || footerSettings.companyLogo2 || null,
        logo2Width: settingsToSave?.logo2Width || footerSettings.logo2Width,
        logo2Height: settingsToSave?.logo2Height || footerSettings.logo2Height,
        showAboutSection: true,
        aboutTitle: t('ecommerceFooterSettings.preview.aboutUs'),
        aboutText: elementStylesToSave.companyDescription?.text ?? null,
        showContactSection: true,
        contactTitle: settingsToSave?.contactTitle || footerSettings.contactTitle,
        showContactEmail: true,
        showContactPhone: true,
        showContactAddress: true,
        // İletişim Bilgileri
        contactEmail: settingsToSave?.contactEmail || footerSettings.contactEmail || null,
        contactPhone: settingsToSave?.contactPhone || footerSettings.contactPhone || null,
        contactAddress: settingsToSave?.contactAddress || footerSettings.contactAddress || null,
        showSocialLinks: socialLinksToSave.length > 0,
        socialLinksTitle: t('ecommerceFooterSettings.socialMedia.title'),
        socialIconSize: settingsToSave?.socialIconSize || footerSettings.socialIconSize,
        showQuickLinks: true,
        quickLinksTitle: settingsToSave?.quickLinksTitle || footerSettings.quickLinksTitle,
        showNewsletterSection: currentFs.showNewsletterSection ?? false,
        newsletterTitle: currentFs.newsletterTitle || null,
        newsletterDescription: currentFs.newsletterDescription || null,
        showBottomBar: true,
        bottomBarBackgroundColor: settingsToSave?.backgroundColor || footerSettings.backgroundColor,
        copyrightText: settingsToSave?.copyrightText || footerSettings.copyrightText || null,
        showPaymentIcons: false,
        companyName: settingsToSave?.companyName || footerSettings.companyName || null,
        companyDescription: elementStylesToSave.companyDescription?.text ?? null,
        elementStylesJson: elementStylesJson,
        socialLinksJson: socialLinksJson,
        quickLinksJson: quickLinksJson,
        quickAccessColumnsJson: quickAccessColumnsJson,
      };
      
      const response = await saveFooterSettingsAPI(data);
      
      if (response.success) {
        console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla kaydedildi');
        if (!footerSettingsId && response.data?.id) {
          setFooterSettingsId(response.data.id);
        }

        setElementStyles(elementStylesToSave);
        
        notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      }
    } catch (error) {
      console.error('❌ [FOOTER SETTINGS] Footer ayarları kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.saveError'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Genel yazı rengini değiştir
  const handleGlobalTextColorChange = (color: string) => {
    const updatedStyles = {
      ...elementStyles,
      companyName: { ...elementStyles.companyName, color },
      companyDescription: { ...elementStyles.companyDescription, color },
      sectionTitle: { ...elementStyles.sectionTitle, color },
      contactInfo: { ...elementStyles.contactInfo, color },
      quickLinks: { ...elementStyles.quickLinks, color },
      copyright: { ...elementStyles.copyright, color }
    };
    setElementStyles(updatedStyles);
    
    // ✅ AUTO-SAVE
    autoSaveFooterSettings({ elementStyles: updatedStyles });
  };

  // Genel arka plan rengini değiştir
  const handleGlobalBackgroundColorChange = (color: string) => {
    console.log('🎨 [FOOTER SETTINGS] Arka plan rengi degistiriliyor:', color);
    const updatedFooterSettings = { ...footerSettings, backgroundColor: color };
    const updatedElementStyles = {
      ...elementStyles,
      backgroundColor: { backgroundColor: color }
    };
    
    setFooterSettings(updatedFooterSettings);
    setElementStyles(updatedElementStyles);
    
    // ✅ AUTO-SAVE
    autoSaveFooterSettings({ 
      ...updatedFooterSettings,
      elementStyles: updatedElementStyles 
    });
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Sosyal Medya İkonu Componenti
  const SocialIcon = ({ platform, icon }: { platform: string, icon: string }) => {
    const platformIcons = {
      instagram: [<InstagramIcon />, <InstagramIcon sx={{ borderRadius: '20%' }} />, <InstagramIcon sx={{ background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)', color: 'white', borderRadius: '20%' }} />, <InstagramIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <InstagramIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }} />],
      facebook: [<FacebookIcon />, <FacebookIcon sx={{ borderRadius: '50%', color: 'white', background: '#1877F2' }}/>, <FacebookIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <FacebookIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <FacebookIcon sx={{ color: '#1877F2' }}/>],
      twitter: [<TwitterIcon />, <TwitterIcon sx={{ color: '#1DA1F2'}}/>, <TwitterIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <TwitterIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <TwitterIcon sx={{ borderRadius: '50%', color: 'white', background: '#1DA1F2', p:0.5 }}/>, <ClearIcon sx={{ fontWeight: 'bold', fontSize: '1.2em' }} />],
      linkedin: [<LinkedInIcon />, <LinkedInIcon sx={{ color: '#0A66C2'}}/>, <LinkedInIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <LinkedInIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <LinkedInIcon sx={{ background: '#0A66C2', color: 'white', borderRadius: 1, p: 0.5 }}/>],
      youtube: [<YouTubeIcon />, <YouTubeIcon sx={{ color: '#FF0000'}}/>, <YouTubeIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <YouTubeIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <YouTubeIcon sx={{ background: '#FF0000', color: 'white', borderRadius: 1, p: 0.5 }}/>],
      pinterest: [<PinterestIcon />, <PinterestIcon sx={{ color: '#E60023'}}/>, <PinterestIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <PinterestIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <PinterestIcon sx={{ background: '#E60023', color: 'white', borderRadius: '50%', p: 0.5 }}/>],
    };

    const iconIndex = parseInt(icon?.replace('icon', ''), 10) - 1;

    // @ts-ignore
    const icons = platformIcons[platform];

    if (!icons || iconIndex < 0 || iconIndex >= icons.length) {
      // Fallback to a default icon if something is wrong
      switch (platform) {
        case 'instagram': return <InstagramIcon />;
        case 'facebook': return <FacebookIcon />;
        case 'twitter': return <TwitterIcon />;
        case 'linkedin': return <LinkedInIcon />;
        case 'youtube': return <YouTubeIcon />;
        case 'pinterest': return <PinterestIcon />;
        default: return null;
      }
    }

    return icons[iconIndex];
  };

  // Sosyal Medya Modalını aç/kapa
  const handleOpenSocialModal = (link: SocialLink | null = null) => {
    setEditingSocialLink(link);
    setIsSocialModalOpen(true);
  };

  const handleCloseSocialModal = () => {
    setIsSocialModalOpen(false);
    setEditingSocialLink(null);
  };

  // Sosyal Medya linkini kaydet/güncelle
  const handleSaveSocialLink = (link: SocialLink) => {
    const updatedLinks = editingSocialLink
      ? footerSettings.socialLinks.map(l => l.id === link.id ? link : l)
      : [...footerSettings.socialLinks, link];
    
    const updatedFooterSettings = { ...footerSettings, socialLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece sosyal linkleri güncelle
    saveFooterSettings(updatedFooterSettings);
    handleCloseSocialModal();
  };

  // Silme modalını aç
  const openDeleteConfirm = (id: string) => {
    setDeletingSocialLinkId(id);
    setIsDeleteConfirmOpen(true);
  };

  // Silme modalını kapat
  const closeDeleteConfirm = () => {
    setDeletingSocialLinkId(null);
    setIsDeleteConfirmOpen(false);
  };

  // Sosyal Medya linkini sil
  const handleDeleteSocialLink = () => {
    if (!deletingSocialLinkId) return;
    const updatedLinks = footerSettings.socialLinks.filter(l => l.id !== deletingSocialLinkId);
    const updatedFooterSettings = { ...footerSettings, socialLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece sosyal linkleri güncelle
    saveFooterSettings(updatedFooterSettings);
    closeDeleteConfirm();
  };


  const getColumnTitle = (column: QuickAccessColumn, index: number) =>
    column.title?.trim() || t('ecommerceFooterSettings.quickAccess.columnLabel', { number: index + 1 });

  const getPreviewQuickAccessHeader = (column: QuickAccessColumn, columnIndex: number) =>
    getQuickAccessColumnHeader(
      column,
      columnIndex,
      footerSettings.quickAccessColumns,
      footerSettings.quickLinksTitle || 'Hızlı Erişim'
    );

  // Hızlı Erişim Linki için Modal Aç/Kapa
  const handleOpenQuickLinkModal = (link: QuickLink | null = null, defaultColumn?: number) => {
    setEditingQuickLink(link);
    setQuickLinkDefaultColumn(link ? undefined : defaultColumn);
    setIsQuickLinkModalOpen(true);
  };

  const handleCloseQuickLinkModal = () => {
    setIsQuickLinkModalOpen(false);
    setEditingQuickLink(null);
    setQuickLinkDefaultColumn(undefined);
  };

  const handleAddQuickAccessColumn = () => {
    const columns = footerSettings.quickAccessColumns;
    if (columns.length >= MAX_QUICK_ACCESS_COLUMNS) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.maxColumnsLimit', { count: MAX_QUICK_ACCESS_COLUMNS }),
        severity: 'error',
      });
      return;
    }

    const nextId = columns.reduce((max, col) => Math.max(max, col.id), 0) + 1;
    const updatedColumns = [
      ...columns,
      {
        id: nextId,
        maxLinks: 10,
        hasFixedLinks: false,
      },
    ];
    const updatedFooterSettings = { ...footerSettings, quickAccessColumns: updatedColumns };
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
  };

  const handleUpdateColumnTitle = (columnId: number, title: string) => {
    setFooterSettings(prev => {
      const updatedColumns = prev.quickAccessColumns.map(col =>
        col.id === columnId ? { ...col, title } : col
      );
      return { ...prev, quickAccessColumns: updatedColumns };
    });
  };

  const handleColumnTitleBlur = (columnId: number, title: string) => {
    setFooterSettings(prev => {
      const updatedColumns = prev.quickAccessColumns.map(col =>
        col.id === columnId ? { ...col, title } : col
      );
      const updatedElementStyles = {
        ...elementStylesRef.current,
        quickAccessColumns: updatedColumns,
      };
      setElementStyles(updatedElementStyles);
      saveFooterSettings({
        quickAccessColumns: updatedColumns,
        elementStyles: updatedElementStyles,
      });
      return { ...prev, quickAccessColumns: updatedColumns };
    });
  };

  const openColumnDeleteConfirm = (columnId: number) => {
    setDeletingColumnId(columnId);
    setIsColumnDeleteConfirmOpen(true);
  };

  const closeColumnDeleteConfirm = () => {
    setDeletingColumnId(null);
    setIsColumnDeleteConfirmOpen(false);
  };

  const handleDeleteQuickAccessColumn = () => {
    if (deletingColumnId == null) return;

    const columns = footerSettings.quickAccessColumns;
    const columnToDelete = columns.find(col => col.id === deletingColumnId);
    if (columnToDelete?.hasFixedLinks) {
      closeColumnDeleteConfirm();
      return;
    }

    if (columns.length <= 1) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.minColumnsRequired'),
        severity: 'error',
      });
      closeColumnDeleteConfirm();
      return;
    }

    const updatedColumns = columns.filter(col => col.id !== deletingColumnId);
    const updatedLinks = footerSettings.quickLinks.filter(link => link.column !== deletingColumnId);
    const updatedFooterSettings = {
      ...footerSettings,
      quickAccessColumns: updatedColumns,
      quickLinks: updatedLinks,
    };
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
    closeColumnDeleteConfirm();
  };

  // Hızlı Erişim Linki Kaydet/Güncelle
  const handleSaveQuickLink = (link: QuickLink) => {
    const targetColumn = footerSettings.quickAccessColumns.find(col => col.id === link.column);
    if (!targetColumn) {
      setSnackbar({ open: true, message: t('ecommerceFooterSettings.quickAccess.invalidColumn'), severity: 'error' });
      return;
    }

    const columnCount = footerSettings.quickLinks.filter(
      l => l.column === link.column && l.id !== link.id
    ).length;

    if (columnCount >= targetColumn.maxLinks) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.columnLimit', { count: targetColumn.maxLinks }),
        severity: 'error',
      });
      return;
    }

    const updatedLinks = editingQuickLink
      ? footerSettings.quickLinks.map(l => l.id === link.id ? link : l)
      : [...footerSettings.quickLinks, link];
    
    const updatedFooterSettings = { ...footerSettings, quickLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece hızlı erişim linklerini güncelle
    saveFooterSettings(updatedFooterSettings);
    handleCloseQuickLinkModal();
  };

  // Hızlı Erişim Linki Silme Onayı
  const openQuickLinkDeleteConfirm = (id: string) => {
    setDeletingQuickLinkId(id);
    setIsQuickLinkDeleteConfirmOpen(true);
  };

  const closeQuickLinkDeleteConfirm = () => {
    setDeletingQuickLinkId(null);
    setIsQuickLinkDeleteConfirmOpen(false);
  };

  // Hızlı Erişim Linkini Sil
  const handleDeleteQuickLink = () => {
    if (!deletingQuickLinkId) return;
    const updatedLinks = footerSettings.quickLinks.filter(l => l.id !== deletingQuickLinkId);
    const updatedFooterSettings = { ...footerSettings, quickLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece hızlı erişim linklerini güncelle
    saveFooterSettings(updatedFooterSettings);
    closeQuickLinkDeleteConfirm();
  };

  // ✅ YENİ: Resim kütüphanesi modalını aç
  const handleOpenImageLibrary = (logoType: 'logo1' | 'logo2' | 'newsletter' | 'newsletterMobile') => {
    setCurrentLogoType(logoType);
    setIsImageLibraryOpen(true);
  };

  // ✅ YENİ: Resim kütüphanesinden resim seçildiğinde
  const handleImageSelect = async (selectedUrls: string[]) => {
    if (selectedUrls.length === 0) return;
    
    const imageUrl = selectedUrls[0];
    console.log('🖼️ [FOOTER SETTINGS] Resim seçildi:', imageUrl, 'Tip:', currentLogoType);
    
    const fieldMap: Record<string, string> = {
      logo1: 'companyLogo',
      logo2: 'companyLogo2',
      newsletter: 'newsletterImage',
      newsletterMobile: 'newsletterMobileImage',
    };
    const field = fieldMap[currentLogoType] || 'companyLogo';
    
    const updatedSettings = { ...footerSettings, [field]: imageUrl };
    setFooterSettings(updatedSettings);
    setIsImageLibraryOpen(false);
    
    await saveFooterSettings(updatedSettings);
    
    const messages: Record<string, string> = {
      logo1: t('ecommerceFooterSettings.messages.logoSelected'),
      logo2: t('ecommerceFooterSettings.messages.secondLogoSelected'),
      newsletter: t('ecommerceFooterSettings.messages.newsletterImageSelected'),
      newsletterMobile: t('ecommerceFooterSettings.messages.newsletterMobileImageSelected'),
    };
    setSnackbar({
      open: true,
      message: messages[currentLogoType] || t('ecommerceFooterSettings.messages.imageSelected'),
      severity: 'success'
    });
  };

  // Logo sil
  const handleRemoveLogo = async () => {
    if (!footerSettings.companyLogo || !companyId) return;

    // Footer ayarlarını güncelle
    const updatedSettings = {
      ...footerSettings,
      companyLogo: ''
    };
    
    setFooterSettings(updatedSettings);
    
    // SQL'e kaydet
    await saveFooterSettings(updatedSettings);
    
    setSnackbar({
      open: true,
      message: t('ecommerceFooterSettings.messages.logoDeleted'),
      severity: 'success'
    });
  };

  // İkinci logo sil
  const handleRemoveLogo2 = async () => {
    if (!footerSettings.companyLogo2 || !companyId) return;

    // Footer ayarlarını güncelle
    const updatedSettings = {
      ...footerSettings,
      companyLogo2: ''
    };
    
    setFooterSettings(updatedSettings);
    
    // SQL'e kaydet
    await saveFooterSettings(updatedSettings);
    
    setSnackbar({
      open: true,
      message: t('ecommerceFooterSettings.messages.secondLogoDeleted'),
      severity: 'success'
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <SectionTitle sx={{ mb: 0 }}>
          <SettingsIcon /> {t('ecommerceFooterSettings.title')}
        </SectionTitle>
        
        {/* ✅ AUTO-SAVE: Kayıt durumu göstergesi */}
        {isSaving && (
          <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>💾</span> {t('ecommerceFooterSettings.messages.saving')}
          </Typography>
        )}
      </Box>
      
      <Box>
        {/* Footer Önizlemesi */}
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('ecommerceFooterSettings.preview.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Footer elementleri üzerinde sağ tık yaparak stilleri düzenleyebilirsiniz.
        </Typography>

        {/* Genel Footer Ayarları */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('ecommerceFooterSettings.generalSettings.title')}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            {/* Genel Yazı Rengi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: '100px' }}>
                {t('ecommerceFooterSettings.generalSettings.textColor')}:
              </Typography>
              <Box
                component="input"
                type="color"
                value={elementStyles.sectionTitle.color}
                onChange={(e: any) => handleGlobalTextColorChange(e.target.value)}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={elementStyles.sectionTitle.color}
                onChange={(e: any) => handleGlobalTextColorChange(e.target.value)}
                placeholder="#ffffff"
                sx={{ 
                  width: '120px',
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1,
                  fontSize: '14px'
                }}
              />
            </Box>

            {/* Genel Arka Plan Rengi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: '100px' }}>
                {t('ecommerceFooterSettings.generalSettings.background')}:
              </Typography>
              <Box
                component="input"
                type="color"
                value={footerSettings.backgroundColor}
                onChange={(e: any) => handleGlobalBackgroundColorChange(e.target.value)}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={footerSettings.backgroundColor}
                onChange={(e: any) => handleGlobalBackgroundColorChange(e.target.value)}
                placeholder="#1f2937"
                sx={{ 
                  width: '120px',
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1,
                  fontSize: '14px'
                }}
              />
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Bu ayarlar tüm footer yazılarının rengini aynı anda değiştirir. İsterseniz her elementi sağ tık ile ayrı ayrı da düzenleyebilirsiniz.
          </Typography>
        </Paper>
        
        {/* Footer Örneği - Birebir aynısı */}
        <Box 
          sx={{ 
            bgcolor: footerSettings.backgroundColor,
            color: '#ffffff',
            py: 4,
            px: 2,
            borderRadius: 1,
            mb: 3,
            cursor: 'context-menu'
          }}
          onContextMenu={(e) => handleContextMenu(e, 'backgroundColor')}
        >
          <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(180px, 280px) 1fr' },
              gap: 4
            }}>
              {/* Şirket Bilgileri */}
              <Box>
                {/* Logo veya Şirket Adı */}
                {footerSettings.companyLogo ? (
                  <Box sx={{ mb: 2, cursor: 'context-menu' }} onContextMenu={(e) => handleContextMenu(e, 'companyName')}>
                    <img
                      src={footerSettings.companyLogo}
                      alt={exampleCompany.name}
                      style={{
                        width: `${footerSettings.logoWidth}px`,
                        height: `${footerSettings.logoHeight}px`,
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </Box>
                ) : (
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2, 
                      ...elementStyles.companyName,
                      cursor: 'context-menu',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                    onContextMenu={(e) => handleContextMenu(e, 'companyName')}
                  >
                    {exampleCompany.name}
                  </Typography>
                )}
                
                {/* İkinci Logo */}
                {footerSettings.companyLogo2 && (
                  <Box sx={{ mb: 2, cursor: 'context-menu' }} onContextMenu={(e) => handleContextMenu(e, 'companyName')}>
                    <img
                      src={footerSettings.companyLogo2}
                      alt={`${exampleCompany.name} - Second Logo`}
                      style={{
                        width: `${footerSettings.logo2Width}px`,
                        height: `${footerSettings.logo2Height}px`,
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </Box>
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ...elementStyles.companyDescription,
                    cursor: 'context-menu',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'companyDescription')}
                >
                  {tempDescription}
                </Typography>

                {/* Sosyal Medya İkonları Önizlemesi */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  {footerSettings.socialLinks.map((link) => (
                    <IconButton 
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      sx={{ 
                        color: elementStyles.quickLinks.color,
                        fontSize: `${footerSettings.socialIconSize}px`
                      }}
                    >
                      <SocialIcon platform={link.platform} icon={link.icon} />
                    </IconButton>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: getFooterLinksGridTemplate(footerSettings.quickAccessColumns.length), gap: 4, minWidth: 0 }}>
              {/* İletişim Bilgileri */}
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    ...elementStyles.sectionTitle,
                    cursor: 'context-menu',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'sectionTitle')}
                >
                  İletişim
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1, 
                    ...elementStyles.contactInfo,
                    cursor: 'context-menu',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                >
                  {exampleCompany.address}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ...elementStyles.contactInfo,
                    cursor: 'context-menu',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                >
                  Telefon: {exampleCompany.phone}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ...elementStyles.contactInfo,
                    cursor: 'context-menu',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                >
                  E-posta: {exampleCompany.email}
                </Typography>
              </Box>
              
              {/* Hızlı Erişim Sütunları */}
              {footerSettings.quickAccessColumns.map((column, columnIndex) => {
                const columnLinks = footerSettings.quickLinks.filter(l => l.column === column.id);
                const columnHeader = getPreviewQuickAccessHeader(column, columnIndex);
                return (
                  <Box key={column.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                    {columnHeader && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.5,
                        ...elementStyles.sectionTitle,
                        fontWeight: 'bold',
                        cursor: 'context-menu',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onContextMenu={(e) => handleContextMenu(e, 'sectionTitle')}
                    >
                      {columnHeader}
                    </Typography>
                    )}
                        {column.hasFixedLinks && (
                          <>
                            <Link
                              href="#"
                              sx={{
                                ...elementStyles.quickLinks,
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                cursor: 'context-menu',
                                '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                              }}
                              onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                            >
                              Ana Sayfa
                            </Link>
                            <Link
                              href="#"
                              sx={{
                                ...elementStyles.quickLinks,
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                cursor: 'context-menu',
                                '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                              }}
                              onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                            >
                              Hakkımızda
                            </Link>
                            <Link
                              href="#"
                              sx={{
                                ...elementStyles.quickLinks,
                                textDecoration: 'none',
                                transition: 'color 0.2s',
                                cursor: 'context-menu',
                                '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                              }}
                              onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                            >
                              İletişim
                            </Link>
                          </>
                        )}
                        {columnLinks.map(link => (
                          <Link
                            key={link.id}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              ...elementStyles.quickLinks,
                              textDecoration: 'none',
                              transition: 'color 0.2s',
                              cursor: 'context-menu',
                              '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                            }}
                            onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </Box>
                    );
                  })}
              </Box>
            </Box>
            
            {/* Copyright */}
            <Box sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid rgba(255,255,255,0.2)', 
              textAlign: 'center' 
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  ...elementStyles.copyright,
                  cursor: 'context-menu',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onContextMenu={(e) => handleContextMenu(e, 'copyright')}
              >
            © {currentYear} {exampleCompany.name}. {t('ecommerceFooterSettings.preview.allRightsReserved')}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Logo Yönetimi */}
        <SectionTitle>
          <ImageIcon /> {t('ecommerceFooterSettings.sections.companyLogoManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Şirket logosunu yükleyerek footer'da şirket adı yerine logo gösterebilirsiniz.
          </Typography>
          
          {/* Mevcut Logo */}
          {footerSettings.companyLogo && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Mevcut Logo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}>
                <img
                  src={footerSettings.companyLogo}
                  alt="Company Logo"
                  style={{
                    width: `${footerSettings.logoWidth}px`,
                    height: `${footerSettings.logoHeight}px`,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Logo boyutu: {footerSettings.logoWidth}x{footerSettings.logoHeight}px
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveLogo}
                  >
                    Logoyu Sil
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* ✅ YENİ: Resim Kütüphanesinden Logo Seç */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {footerSettings.companyLogo ? t('ecommerceFooterSettings.actions.changeLogo') : t('ecommerceFooterSettings.actions.selectLogo')}
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleOpenImageLibrary('logo1')}
            >
              Resim Kütüphanesinden Seç
            </Button>
          </Box>

          {/* Logo Boyut Ayarları */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Logo Boyut Ayarları
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {/* Genişlik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Genişlik: {footerSettings.logoWidth}px
                </Typography>
                <Slider
                  value={footerSettings.logoWidth}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logoWidth: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logoWidth: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo-width-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={50}
                  max={300}
                />
              </Box>
              {/* Yükseklik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Yükseklik: {footerSettings.logoHeight}px
                </Typography>
                <Slider
                  value={footerSettings.logoHeight}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logoHeight: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logoHeight: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo-height-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={30}
                  max={150}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* İkinci Logo Yönetimi */}
        <SectionTitle>
          <ImageIcon /> {t('ecommerceFooterSettings.sections.secondCompanyLogoManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            İkinci şirket logosunu yükleyerek footer'da ana logonun altında gösterebilirsiniz.
          </Typography>
          
          {/* Mevcut İkinci Logo */}
          {footerSettings.companyLogo2 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Mevcut İkinci Logo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}>
                <img
                  src={footerSettings.companyLogo2}
                  alt="Second Company Logo"
                  style={{
                    width: `${footerSettings.logo2Width}px`,
                    height: `${footerSettings.logo2Height}px`,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Logo boyutu: {footerSettings.logo2Width}x{footerSettings.logo2Height}px
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveLogo2}
                  >
                    İkinci Logoyu Sil
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* ✅ YENİ: Resim Kütüphanesinden İkinci Logo Seç */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {footerSettings.companyLogo2
                ? t('ecommerceFooterSettings.actions.changeLogo')
                : t('ecommerceFooterSettings.actions.selectLogo')}
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleOpenImageLibrary('logo2')}
            >
              Resim Kütüphanesinden Seç
            </Button>
          </Box>

          {/* İkinci Logo Boyut Ayarları */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              İkinci Logo Boyut Ayarları
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {/* Genişlik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Genişlik: {footerSettings.logo2Width}px
                </Typography>
                <Slider
                  value={footerSettings.logo2Width}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logo2Width: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logo2Width: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo2-width-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={50}
                  max={300}
                />
              </Box>
              {/* Yükseklik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Yükseklik: {footerSettings.logo2Height}px
                </Typography>
                <Slider
                  value={footerSettings.logo2Height}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logo2Height: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logo2Height: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo2-height-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={30}
                  max={150}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
        
        {/* İletişim Bilgileri Yönetimi */}
        <SectionTitle>
          <SettingsIcon /> {t('ecommerceFooterSettings.sections.contactInformationManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Footer'da gösterilecek iletişim bilgilerini buradan yönetebilirsiniz.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* E-posta */}
            <TextField
              fullWidth
              label={t('ecommerceFooterSettings.fields.email')}
              type="email"
              value={footerSettings.contactEmail}
              onChange={(e) => {
                const updatedSettings = { ...footerSettings, contactEmail: e.target.value };
                setFooterSettings(updatedSettings);
              }}
              placeholder="info@company.com"
              helperText={t('ecommerceFooterSettings.helpers.emailShownInFooter')}
            />
            
            {/* Telefon */}
            <TextField
              fullWidth
              label={t('ecommerceFooterSettings.fields.phone')}
              type="tel"
              value={footerSettings.contactPhone}
              onChange={(e) => {
                const updatedSettings = { ...footerSettings, contactPhone: e.target.value };
                setFooterSettings(updatedSettings);
              }}
              placeholder="+90 (212) 123 45 67"
              helperText={t('ecommerceFooterSettings.helpers.phoneShownInFooter')}
            />
            
            {/* Adres */}
            <TextField
              fullWidth
              label={t('ecommerceFooterSettings.fields.address')}
              multiline
              rows={3}
              value={footerSettings.contactAddress}
              onChange={(e) => {
                const updatedSettings = { ...footerSettings, contactAddress: e.target.value };
                setFooterSettings(updatedSettings);
              }}
              placeholder={t('ecommerceFooterSettings.preview.companyAddress')}
              helperText={t('ecommerceFooterSettings.helpers.addressShownInFooter')}
            />
          </Box>
        </Paper>

        {/* Sosyal Medya Yönetimi */}
        <SectionTitle>{t('ecommerceFooterSettings.sections.socialMediaManagement')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ minWidth: '100px' }}>
              {t('ecommerceFooterSettings.socialMedia.iconSize')}:
            </Typography>
            <Slider
              value={footerSettings.socialIconSize}
              onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, socialIconSize: newValue as number }))}
              onChangeCommitted={(e, newValue) => {
                const updatedSettings = { ...footerSettings, socialIconSize: newValue as number };
                setFooterSettings(updatedSettings);
                autoSaveFooterSettings(updatedSettings);
              }}
              aria-labelledby="social-icon-size-slider"
              valueLabelDisplay="auto"
              step={1}
              marks
              min={16}
              max={48}
              sx={{ maxWidth: 300 }}
            />
            <Typography sx={{ minWidth: '30px' }}>{footerSettings.socialIconSize}px</Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <List dense>
            {footerSettings.socialLinks.length > 0 ? footerSettings.socialLinks.map((link) => (
              <ListItem
                key={link.id}
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" sx={{ mr: 1 }} onClick={() => handleOpenSocialModal(link)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => openDeleteConfirm(link.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemIcon sx={{ fontSize: `${footerSettings.socialIconSize}px` }}>
                  <SocialIcon platform={link.platform} icon={link.icon} />
                </ListItemIcon>
                <ListItemText
                  primary={link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                  secondary={link.url}
                />
              </ListItem>
            )) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                {t('ecommerceFooterSettings.socialMedia.noLinks')}
              </Typography>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpenSocialModal()}
          >
            {t('ecommerceFooterSettings.actions.addSocialLink')}
          </Button>
        </Paper>

        {/* Hızlı Erişim Yönetimi */}
        <SectionTitle>{t('ecommerceFooterSettings.sections.quickAccessManagement')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: getQuickAccessGridTemplate(footerSettings.quickAccessColumns.length), gap: 2 }}>
            {footerSettings.quickAccessColumns.map((column, columnIndex) => {
              const columnLinks = footerSettings.quickLinks.filter(l => l.column === column.id);
              return (
                <Box key={column.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label={t('ecommerceFooterSettings.quickAccess.columnTitleLabel')}
                      placeholder={t('ecommerceFooterSettings.quickAccess.columnTitlePlaceholder', { number: columnIndex + 1 })}
                      value={column.title ?? ''}
                      onChange={(e) => handleUpdateColumnTitle(column.id, e.target.value)}
                      onBlur={(e) => handleColumnTitleBlur(column.id, e.target.value)}
                    />
                    {footerSettings.quickAccessColumns.length > 1 && !column.hasFixedLinks && (
                      <IconButton
                        size="small"
                        aria-label="delete column"
                        onClick={() => openColumnDeleteConfirm(column.id)}
                        sx={{ mt: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {column.hasFixedLinks
                      ? t('ecommerceFooterSettings.quickAccess.column1Hint')
                      : t('ecommerceFooterSettings.quickAccess.columnHint', { count: column.maxLinks })}
                  </Typography>
                  <List dense>
                    {column.hasFixedLinks && (
                      <>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.quickAccess.home')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.preview.aboutUs')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.quickAccess.contact')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                      </>
                    )}
                    {columnLinks.length > 0 ? columnLinks.map(link => (
                      <ListItem
                        key={link.id}
                        secondaryAction={
                          <>
                            <IconButton size="small" edge="end" aria-label="edit" sx={{ mr: 0.5 }} onClick={() => handleOpenQuickLinkModal(link)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" edge="end" aria-label="delete" onClick={() => openQuickLinkDeleteConfirm(link.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        }
                      >
                        <ListItemText primary={link.label} secondary={link.url} />
                      </ListItem>
                    )) : !column.hasFixedLinks && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        {t('ecommerceFooterSettings.quickAccess.noLinksInColumn')}
                      </Typography>
                    )}
                  </List>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleOpenQuickLinkModal(null, column.id)}
                    sx={{ mt: 1 }}
                  >
                    {t('ecommerceFooterSettings.actions.addQuickAccessLink')}
                  </Button>
                </Box>
              );
            })}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddQuickAccessColumn}
            disabled={footerSettings.quickAccessColumns.length >= MAX_QUICK_ACCESS_COLUMNS}
          >
            {t('ecommerceFooterSettings.actions.addQuickAccessColumn')}
          </Button>
        </Paper>

        {/* Bülten / Newsletter Bölümü */}
        <SectionTitle>
          <MailOutlineIcon /> {t('ecommerceFooterSettings.sections.newsletter')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.newsletter.descriptionText')}
          </Typography>

          {/* Açma/Kapama */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('ecommerceFooterSettings.newsletter.showSection')}:</Typography>
            <Box
              component="input"
              type="checkbox"
              checked={footerSettings.showNewsletterSection}
              onChange={(e: any) => {
                const updated = { ...footerSettings, showNewsletterSection: e.target.checked };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              sx={{ width: 20, height: 20, cursor: 'pointer' }}
            />
            <Typography variant="body2" color="text.secondary">
              {footerSettings.showNewsletterSection ? t('ecommerceFooterSettings.newsletter.active') : t('ecommerceFooterSettings.newsletter.passive')}
            </Typography>
          </Box>

          {footerSettings.showNewsletterSection && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Başlık */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.titleLabel')}
                value={footerSettings.newsletterTitle}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterTitle: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.titlePlaceholder')}
                helperText={t('ecommerceFooterSettings.newsletter.titleHelper')}
              />

              {/* Açıklama */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.descriptionLabel')}
                value={footerSettings.newsletterDescription}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterDescription: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.descriptionPlaceholder')}
                helperText={t('ecommerceFooterSettings.newsletter.descriptionHelper')}
              />

              {/* Buton Metni */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.buttonTextLabel')}
                value={footerSettings.newsletterButtonText}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterButtonText: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.subscribe')}
                helperText={t('ecommerceFooterSettings.newsletter.buttonTextHelper')}
              />

              {/* Cinsiyet Seçimi */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.showGenderSelection')}:</Typography>
                <Box
                  component="input"
                  type="checkbox"
                  checked={footerSettings.showGenderSelection}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, showGenderSelection: e.target.checked };
                    setFooterSettings(updated);
                    autoSaveFooterSettings(updated);
                  }}
                  sx={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.newsletter.genderSelectionHint')}
                </Typography>
              </Box>

              {/* Arka Plan Rengi */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ minWidth: '120px' }}>{t('ecommerceFooterSettings.newsletter.backgroundColor')}:</Typography>
                <Box
                  component="input"
                  type="color"
                  value={footerSettings.newsletterBgColor}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterBgColor: e.target.value };
                    setFooterSettings(updated);
                  }}
                  onBlur={() => autoSaveFooterSettings()}
                  sx={{ width: '50px', height: '30px', border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer' }}
                />
                <Box
                  component="input"
                  type="text"
                  value={footerSettings.newsletterBgColor}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterBgColor: e.target.value };
                    setFooterSettings(updated);
                  }}
                  onBlur={() => autoSaveFooterSettings()}
                  sx={{ width: '120px', p: 1, border: '1px solid #ccc', borderRadius: 1, fontSize: '14px' }}
                />
              </Box>

              {/* Masaüstü Görsel Seçimi */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t('ecommerceFooterSettings.newsletter.desktopImage')}</Typography>
              {footerSettings.newsletterImage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <img
                    src={footerSettings.newsletterImage}
                    alt="Newsletter image"
                    style={{
                      width: `${Math.min(footerSettings.newsletterImageWidth, 200)}px`,
                      height: `${Math.min(footerSettings.newsletterImageHeight, 150)}px`,
                      objectFit: 'cover',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('ecommerceFooterSettings.newsletter.size')}: {footerSettings.newsletterImageWidth}x{footerSettings.newsletterImageHeight}px
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={async () => {
                        const updated = { ...footerSettings, newsletterImage: '' };
                        setFooterSettings(updated);
                        await saveFooterSettings(updated);
                        setSnackbar({ open: true, message: t('ecommerceFooterSettings.messages.newsletterImageDeleted'), severity: 'success' });
                      }}
                    >
                      {t('ecommerceFooterSettings.actions.deleteImage')}
                    </Button>
                  </Box>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => handleOpenImageLibrary('newsletter')}
              >
                {footerSettings.newsletterImage ? t('ecommerceFooterSettings.actions.changeImage') : t('ecommerceFooterSettings.actions.selectImage')}
              </Button>

              {/* Masaüstü Görsel Boyut Ayarları */}
              {footerSettings.newsletterImage && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.newsletter.width')}: {footerSettings.newsletterImageWidth}px</Typography>
                    <Slider
                      value={footerSettings.newsletterImageWidth}
                      onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterImageWidth: v as number }))}
                      onChangeCommitted={(_e, v) => {
                        const updated = { ...footerSettings, newsletterImageWidth: v as number };
                        setFooterSettings(updated);
                        autoSaveFooterSettings(updated);
                      }}
                      min={100} max={800} step={10}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.newsletter.height')}: {footerSettings.newsletterImageHeight}px</Typography>
                    <Slider
                      value={footerSettings.newsletterImageHeight}
                      onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterImageHeight: v as number }))}
                      onChangeCommitted={(_e, v) => {
                        const updated = { ...footerSettings, newsletterImageHeight: v as number };
                        setFooterSettings(updated);
                        autoSaveFooterSettings(updated);
                      }}
                      min={100} max={600} step={10}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Box>
              )}

              {/* Mobil Ayarlar */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneAndroidIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                {t('ecommerceFooterSettings.newsletter.mobileSettings')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t('ecommerceFooterSettings.newsletter.mobileSettingsHint')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.showImageOnMobile')}:</Typography>
                <Box
                  component="input"
                  type="checkbox"
                  checked={footerSettings.newsletterShowImageOnMobile}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterShowImageOnMobile: e.target.checked };
                    setFooterSettings(updated);
                    autoSaveFooterSettings(updated);
                  }}
                  sx={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.newsletter.showImageOnMobileHint')}
                </Typography>
              </Box>

              {footerSettings.newsletterShowImageOnMobile && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                    {t('ecommerceFooterSettings.newsletter.mobileImage')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceFooterSettings.newsletter.mobileImageHint')}
                  </Typography>
                  {footerSettings.newsletterMobileImage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                      <img
                        src={footerSettings.newsletterMobileImage}
                        alt="Newsletter mobile"
                        style={{
                          width: `${Math.min(footerSettings.newsletterMobileImageWidth, 160)}px`,
                          height: `${Math.min(footerSettings.newsletterMobileImageHeight, 120)}px`,
                          objectFit: 'cover',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {t('ecommerceFooterSettings.newsletter.size')}: {footerSettings.newsletterMobileImageWidth}x{footerSettings.newsletterMobileImageHeight}px
                        </Typography>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={async () => {
                            const updated = { ...footerSettings, newsletterMobileImage: '' };
                            setFooterSettings(updated);
                            await saveFooterSettings(updated);
                            setSnackbar({ open: true, message: t('ecommerceFooterSettings.messages.newsletterMobileImageDeleted'), severity: 'success' });
                          }}
                        >
                          {t('ecommerceFooterSettings.actions.deleteImage')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<PhoneAndroidIcon />}
                    onClick={() => handleOpenImageLibrary('newsletterMobile')}
                  >
                    {footerSettings.newsletterMobileImage
                      ? t('ecommerceFooterSettings.actions.changeMobileImage')
                      : t('ecommerceFooterSettings.actions.selectMobileImage')}
                  </Button>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('ecommerceFooterSettings.newsletter.mobileWidth')}: {footerSettings.newsletterMobileImageWidth}px
                      </Typography>
                      <Slider
                        value={footerSettings.newsletterMobileImageWidth}
                        onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterMobileImageWidth: v as number }))}
                        onChangeCommitted={(_e, v) => {
                          const updated = { ...footerSettings, newsletterMobileImageWidth: v as number };
                          setFooterSettings(updated);
                          autoSaveFooterSettings(updated);
                        }}
                        min={80} max={400} step={10}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('ecommerceFooterSettings.newsletter.mobileHeight')}: {footerSettings.newsletterMobileImageHeight}px
                      </Typography>
                      <Slider
                        value={footerSettings.newsletterMobileImageHeight}
                        onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterMobileImageHeight: v as number }))}
                        onChangeCommitted={(_e, v) => {
                          const updated = { ...footerSettings, newsletterMobileImageHeight: v as number };
                          setFooterSettings(updated);
                          autoSaveFooterSettings(updated);
                        }}
                        min={80} max={400} step={10}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* Önizleme */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceFooterSettings.preview.title')}</Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 4,
                p: 4,
                bgcolor: footerSettings.newsletterBgColor,
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <Box sx={{ flex: 1, minWidth: 280 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontStyle: 'italic' }}>
                    {footerSettings.newsletterTitle || t('ecommerceFooterSettings.newsletter.titleNotProvided')}
                  </Typography>
                  {footerSettings.newsletterDescription && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {footerSettings.newsletterDescription}
                    </Typography>
                  )}
                  {footerSettings.showGenderSelection && (
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="input" type="radio" name="preview-gender" defaultChecked sx={{ cursor: 'pointer' }} />
                        <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.female')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="input" type="radio" name="preview-gender" sx={{ cursor: 'pointer' }} />
                        <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.male')}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      component="input"
                      type="email"
                      placeholder={t('ecommerceFooterSettings.newsletter.emailPlaceholder')}
                      disabled
                      sx={{ flex: 1, p: 1.5, border: '1px solid #ccc', borderRadius: 0, fontSize: '14px', bgcolor: '#fff' }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', letterSpacing: 2, fontWeight: 500 }}>
                    {footerSettings.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe')}
                  </Typography>
                </Box>
                {footerSettings.newsletterImage && (
                  <Box sx={{ flexShrink: 0, marginRight: `${footerSettings.newsletterImageSpacing}px` }}>
                    <img
                      src={footerSettings.newsletterImage}
                      alt="Newsletter"
                      style={{
                        width: `${Math.min(footerSettings.newsletterImageWidth, 250)}px`,
                        height: `${Math.min(footerSettings.newsletterImageHeight, 200)}px`,
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Mobil Önizleme */}
              {footerSettings.newsletterShowImageOnMobile && (
                <Box sx={{
                  mt: 2,
                  maxWidth: 360,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 3,
                  bgcolor: footerSettings.newsletterBgColor,
                  borderRadius: 1,
                  border: '1px dashed #90caf9',
                  alignItems: 'stretch',
                }}>
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <PhoneAndroidIcon sx={{ fontSize: 14 }} />
                    {t('ecommerceFooterSettings.newsletter.mobilePreview')}
                  </Typography>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontStyle: 'italic', fontSize: '1rem' }}>
                      {footerSettings.newsletterTitle || t('ecommerceFooterSettings.newsletter.titleNotProvided')}
                    </Typography>
                    {footerSettings.newsletterDescription && (
                      <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary', display: 'block' }}>
                        {footerSettings.newsletterDescription}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ letterSpacing: 1, fontWeight: 500 }}>
                      {footerSettings.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe')}
                    </Typography>
                  </Box>
                  {(footerSettings.newsletterMobileImage || footerSettings.newsletterImage) && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={footerSettings.newsletterMobileImage || footerSettings.newsletterImage}
                        alt="Newsletter mobile preview"
                        style={{
                          width: `${Math.min(footerSettings.newsletterMobileImageWidth, 280)}px`,
                          maxWidth: '100%',
                          height: `${Math.min(footerSettings.newsletterMobileImageHeight, 200)}px`,
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>

      </Box>

      {/* Sağ tık menüsü */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditClick}>{t('ecommerceFooterSettings.modals.edit')}</MenuItem>
      </Menu>

      {/* Stil düzenleme modalı */}
      {editingElementKey && editingElementKey !== 'backgroundColor' && (
        <ElementStyleEditorModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          elementKey={editingElementKey}
          currentStyles={elementStyles[editingElementKey]}
          onSave={handleStyleSave}
        />
      )}

      {/* Arka plan rengi düzenleme modalı */}
      <Dialog 
        open={isBackgroundModalOpen} 
        onClose={() => setIsBackgroundModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.footerBackgroundColorTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerceFooterSettings.modals.footerBackgroundColorDescription')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="input"
              type="color"
              value={tempBackgroundColor}
              onChange={(e: any) => setTempBackgroundColor(e.target.value)}
              sx={{ 
                width: '80px', 
                height: '40px', 
                border: 'none', 
                borderRadius: 1, 
                cursor: 'pointer' 
              }}
            />
            <Box
              component="input"
              type="text"
              value={tempBackgroundColor}
              onChange={(e: any) => setTempBackgroundColor(e.target.value)}
              placeholder="#1f2937"
              sx={{ 
                flexGrow: 1, 
                p: 1, 
                border: '1px solid #ccc', 
                borderRadius: 1 
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBackgroundModalOpen(false)}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button 
            onClick={handleBackgroundColorSave} 
            variant="contained"
          >
            {t('ecommerceFooterSettings.modals.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şirket açıklaması düzenleme modalı */}
      <Dialog 
        open={isDescriptionModalOpen} 
        onClose={() => setIsDescriptionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.editCompanyDescriptionTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.modals.editCompanyDescriptionDescription')}
          </Typography>
          
          {/* Metin Düzenleme Alanı */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.modals.descriptionText')}</Typography>
            <Box
              component="textarea"
              value={tempDescription}
              onChange={(e: any) => {
                if (e.target.value.length <= 500) {
                  setTempDescription(e.target.value);
                }
              }}
              placeholder={t('ecommerceFooterSettings.modals.descriptionPlaceholder')}
              sx={{ 
                width: '100%',
                minHeight: '100px',
                p: 2, 
                border: '1px solid #ccc', 
                borderRadius: 1,
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceFooterSettings.modals.charCount', { count: tempDescription.length, max: 500 })}
            </Typography>
          </Box>

          {/* Stil Düzenleme */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('ecommerceFooterSettings.modals.textStyle')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Font Boyutu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '80px' }}>{t('ecommerceFooterSettings.modals.size')}</Typography>
              <Box
                component="input"
                type="range"
                min="10"
                max="24"
                step="1"
                value={parseInt(tempDescriptionStyles.fontSize.replace('rem', '').replace('px', ''))}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  fontSize: e.target.value + 'px' 
                }))}
                sx={{ flexGrow: 1 }}
              />
              <Typography sx={{ minWidth: '40px' }}>
                {tempDescriptionStyles.fontSize}
              </Typography>
            </Box>

            {/* Renk */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '80px' }}>{t('ecommerceFooterSettings.modals.color')}</Typography>
              <Box
                component="input"
                type="color"
                value={tempDescriptionStyles.color}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  color: e.target.value 
                }))}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: 'none', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={tempDescriptionStyles.color}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  color: e.target.value 
                }))}
                sx={{ 
                  flexGrow: 1, 
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1 
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDescriptionModalOpen(false)}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button 
            onClick={handleDescriptionSave} 
            variant="contained"
          >
            {t('ecommerceFooterSettings.modals.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Sosyal Medya Silme Onay Modalı */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('ecommerceFooterSettings.modals.deleteConfirmationTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('ecommerceFooterSettings.modals.deleteSocialLinkConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteSocialLink} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hızlı Erişim Sütun Silme Onay Modalı */}
      <Dialog
        open={isColumnDeleteConfirmOpen}
        onClose={closeColumnDeleteConfirm}
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.deleteColumnTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ecommerceFooterSettings.modals.deleteColumnDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnDeleteConfirm}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button onClick={handleDeleteQuickAccessColumn} color="error" autoFocus>
            {t('ecommerceFooterSettings.modals.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hızlı Erişim Linki Silme Onay Modalı */}
      <Dialog
        open={isQuickLinkDeleteConfirmOpen}
        onClose={closeQuickLinkDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Confirmation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to permanently delete this quick access link? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuickLinkDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteQuickLink} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sosyal Medya Ekleme/Düzenleme Modalı */}
      <SocialMediaModal
        open={isSocialModalOpen}
        onClose={handleCloseSocialModal}
        onSave={handleSaveSocialLink}
        existingLink={editingSocialLink}
      />

      {/* Hızlı Erişim Linki Ekleme/Düzenleme Modalı */}
      <QuickAccessLinkModal
        open={isQuickLinkModalOpen}
        onClose={handleCloseQuickLinkModal}
        onSave={handleSaveQuickLink}
        existingLink={editingQuickLink}
        columns={footerSettings.quickAccessColumns}
        defaultColumn={quickLinkDefaultColumn}
        getColumnTitle={getColumnTitle}
      />

      {/* ✅ YENİ: Resim Kütüphanesi Modalı */}
      <ImageLibrarySelectionModal
        open={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        onSave={handleImageSelect}
        companyId={companyId}
        productName={
          currentLogoType === 'newsletter'
            ? t('ecommerceFooterSettings.imageNames.newsletterImage')
            : currentLogoType === 'newsletterMobile'
              ? t('ecommerceFooterSettings.imageNames.newsletterMobileImage')
              : currentLogoType === 'logo1'
                ? t('ecommerceFooterSettings.imageNames.footerLogo1')
                : t('ecommerceFooterSettings.imageNames.footerLogo2')
        }
        currentImageUrls={
          currentLogoType === 'newsletter'
            ? (footerSettings.newsletterImage ? [footerSettings.newsletterImage] : [])
            : currentLogoType === 'newsletterMobile'
              ? (footerSettings.newsletterMobileImage ? [footerSettings.newsletterMobileImage] : [])
              : currentLogoType === 'logo1'
                ? (footerSettings.companyLogo ? [footerSettings.companyLogo] : [])
                : (footerSettings.companyLogo2 ? [footerSettings.companyLogo2] : [])
        }
        productId={null}
      />
    </Paper>
  );
};

// SocialMediaModal Component (within the same file)
interface SocialMediaModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: SocialLink) => void;
  existingLink: SocialLink | null;
}

const socialPlatforms = [
  { value: 'instagram', label: 'Instagram', icon: <InstagramIcon /> },
  { value: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
  { value: 'twitter', label: 'Twitter / X', icon: <TwitterIcon /> },
  { value: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon /> },
  { value: 'youtube', label: 'YouTube', icon: <YouTubeIcon /> },
  { value: 'pinterest', label: 'Pinterest', icon: <PinterestIcon /> },
];

const SocialMediaModal: React.FC<SocialMediaModalProps> = ({ open, onClose, onSave, existingLink }) => {
  const { t } = useTranslation('salesSystem');
  const [platform, setPlatform] = useState<'instagram' | 'facebook' | 'twitter' | 'linkedin' | 'youtube' | 'pinterest'>('instagram');
  const [url, setUrl] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('icon1'); // 'icon1' to 'icon5'

  useEffect(() => {
    if (existingLink) {
      setPlatform(existingLink.platform);
      setUrl(existingLink.url);
      setSelectedIcon(existingLink.icon);
    } else {
      // Reset form for new entry
      setPlatform('instagram');
      setUrl('');
      setSelectedIcon('icon1');
    }
  }, [existingLink, open]);

  const handleSubmit = () => {
    if (!url.trim()) return;
    onSave({
      id: existingLink?.id || new Date().toISOString(),
      platform,
      icon: selectedIcon,
      url,
    });
  };

  const platformIcons = {
    instagram: [<InstagramIcon />, <InstagramIcon sx={{ borderRadius: '20%' }} />, <InstagramIcon sx={{ background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)', color: 'white', borderRadius: '20%' }} />, <InstagramIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <InstagramIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }} />, <InstagramIcon sx={{ fontSize: '1.2em', fontWeight: 'bold' }} />],
    facebook: [<FacebookIcon />, <FacebookIcon sx={{ borderRadius: '50%', color: 'white', background: '#1877F2' }}/>, <FacebookIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <FacebookIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <FacebookIcon sx={{ color: '#1877F2' }}/>, <FacebookIcon sx={{ fontSize: '1.2em', fontWeight: 'bold' }} />],
    twitter: [<TwitterIcon />, <TwitterIcon sx={{ color: '#1DA1F2'}}/>, <TwitterIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <TwitterIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <TwitterIcon sx={{ borderRadius: '50%', color: 'white', background: '#1DA1F2', p:0.5 }}/>, <ClearIcon sx={{ fontWeight: 'bold', fontSize: '1.2em' }} />],
    linkedin: [<LinkedInIcon />, <LinkedInIcon sx={{ color: '#0A66C2'}}/>, <LinkedInIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <LinkedInIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <LinkedInIcon sx={{ background: '#0A66C2', color: 'white', borderRadius: 1, p: 0.5 }}/>, <LinkedInIcon sx={{ fontSize: '1.2em', fontWeight: 'bold' }} />],
    youtube: [<YouTubeIcon />, <YouTubeIcon sx={{ color: '#FF0000'}}/>, <YouTubeIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <YouTubeIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <YouTubeIcon sx={{ background: '#FF0000', color: 'white', borderRadius: 1, p: 0.5 }}/>, <YouTubeIcon sx={{ fontSize: '1.2em', fontWeight: 'bold' }} />],
    pinterest: [<PinterestIcon />, <PinterestIcon sx={{ color: '#E60023'}}/>, <PinterestIcon sx={{ background: 'black', color: 'white', p: 0.5, borderRadius: 1 }}/>, <PinterestIcon sx={{ border: '1px solid', p: 0.5, borderRadius: 1 }}/>, <PinterestIcon sx={{ background: '#E60023', color: 'white', borderRadius: '50%', p: 0.5 }}/>, <PinterestIcon sx={{ fontSize: '1.2em', fontWeight: 'bold' }} />],
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existingLink ? t('ecommerceFooterSettings.modals.editSocialMediaLink') : t('ecommerceFooterSettings.modals.addSocialMediaLink')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Platform</InputLabel>
          <Select
            value={platform}
            label="Platform"
            onChange={(e) => setPlatform(e.target.value as any)}
          >
            {socialPlatforms.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
        
        <Box my={2}>
          <Typography variant="subtitle2" gutterBottom>Icon Selection</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {/* @ts-ignore */}
            {platformIcons[platform].map((Icon, index) => (
              <IconButton 
                key={index} 
                onClick={() => setSelectedIcon(`icon${index + 1}`)}
                sx={{ border: selectedIcon === `icon${index + 1}` ? '2px solid' : '1px solid', borderColor: selectedIcon === `icon${index + 1}` ? 'primary.main' : 'divider', p: 1 }}
              >
                {Icon}
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box
          component="input"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://instagram.com/username"
          sx={{ 
            width: '100%',
            p: 2, 
            border: '1px solid #ccc', 
            borderRadius: 1,
            fontFamily: 'inherit',
            fontSize: '16px',
            mt: 2
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!url.trim()}>
          {existingLink ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// QuickAccessLinkModal Component
interface QuickAccessLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: QuickLink) => void;
  existingLink: QuickLink | null;
  columns: QuickAccessColumn[];
  defaultColumn?: number;
  getColumnTitle: (column: QuickAccessColumn, index: number) => string;
}

const QuickAccessLinkModal: React.FC<QuickAccessLinkModalProps> = ({
  open,
  onClose,
  onSave,
  existingLink,
  columns,
  defaultColumn,
  getColumnTitle,
}) => {
  const { t } = useTranslation('salesSystem');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [column, setColumn] = useState<number>(columns[0]?.id ?? 1);

  useEffect(() => {
    if (existingLink) {
      setLabel(existingLink.label);
      setUrl(existingLink.url);
      setColumn(existingLink.column);
    } else {
      setLabel('');
      setUrl('');
      setColumn(defaultColumn ?? columns[0]?.id ?? 1);
    }
  }, [existingLink, open, defaultColumn, columns]);

  const handleSubmit = () => {
    if (!label.trim() || !url.trim()) return;
    onSave({
      id: existingLink?.id || new Date().toISOString(),
      label,
      url,
      column,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existingLink ? t('ecommerceFooterSettings.modals.editQuickAccessLink') : t('ecommerceFooterSettings.modals.addQuickAccessLink')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('ecommerceFooterSettings.modals.displayName')}
          type="text"
          fullWidth
          variant="outlined"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          margin="dense"
          label={t('ecommerceFooterSettings.modals.urlLink')}
          type="url"
          fullWidth
          variant="outlined"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth>
          <InputLabel>{t('ecommerceFooterSettings.modals.column')}</InputLabel>
          <Select
            value={column}
            label={t('ecommerceFooterSettings.modals.column')}
            onChange={(e) => setColumn(Number(e.target.value))}
          >
            {columns.map((col, index) => (
              <MenuItem key={col.id} value={col.id}>
                {getColumnTitle(col, index)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!label.trim() || !url.trim()}>
          {existingLink ? t('ecommerceFooterSettings.modals.update') : t('ecommerceFooterSettings.modals.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FooterSettings; 